import CryptoJS from 'crypto-js'

// TypeScript declarations for Google Analytics gtag API
declare global {
    interface Window {
        gtag?: (
            command: 'event' | 'config',
            targetId: string,
            config?: Record<string, any>
        ) => void;
    }
}

export type UserRole = 'admin' | 'insider';
export type AccessLevel = 'admin' | 'insider';

export type User = {
    name:string;
    token:string;
    role?:UserRole;
}

/**
 * Returns the effective role of a user.
 */
export function getUserRole(user: User | undefined | null): UserRole | 'user' | 'anonymous' {
    if (!user) return 'anonymous';
    if (user.role) return user.role;
    return 'user';
}

const rolePriority: Record<string, number> = {
    'anonymous': 0,
    'user': 1,
    'insider': 2,
    'admin': 3,
};

/**
 * Checks if a user has sufficient access for content with a given access level.
 * No access level = visible to everyone.
 */
export function hasAccess(user: User | undefined | null, access?: AccessLevel): boolean {
    if (!access) return true;
    return rolePriority[getUserRole(user)] >= rolePriority[access];
}

/**
 * Gets the access level of content.
 */
export function getContentAccess(frontmatter: { access?: AccessLevel }): AccessLevel | undefined {
    return frontmatter.access;
}

/**
 * Returns a display label for the content access level (for badges).
 */
export function getAccessLabel(access?: AccessLevel): string | undefined {
    if (access === 'admin') return 'Admin';
    if (access === 'insider') return 'Insider';
    return undefined;
}

export var user:User;

var loadingUser:Promise<User> | null;

// Session tracking key for sessionStorage
const SESSION_START_KEY = 'mahanaya_session_started';

/**
 * Checks if an auth_session_start event has already been tracked in this browser session
 * @returns true if auth_session_start was already sent, false otherwise
 */
function hasSessionStarted(): boolean
{
    if (typeof window === "undefined" || !window.sessionStorage)
    {
        return false;
    }

    try
    {
        return window.sessionStorage.getItem(SESSION_START_KEY) === "1";
    }
    catch (error)
    {
        // Fail silently if sessionStorage is unavailable (privacy mode, etc.)
        return false;
    }
}

/**
 * Marks that an auth_session_start event has been sent in this browser session
 */
function markSessionStarted(): void
{
    if (typeof window === "undefined" || !window.sessionStorage)
    {
        return;
    }

    try
    {
        window.sessionStorage.setItem(SESSION_START_KEY, "1");
    }
    catch (error)
    {
        // Fail silently if sessionStorage is unavailable
    }
}

/**
 * Tracks user events to Google Analytics 4
 * @param eventName - The event type ('login' or 'auth_session_start')
 * @param userId - The hashed user ID (MD5)
 * @param userName - The user's display name
 */
function trackUserEvent(eventName: 'login' | 'auth_session_start', userId: string, userName: string): void
{
    if (typeof window === "undefined" || !window.gtag)
    {
        console.warn(`[GA4 Tracking] gtag not available, unable to track ${eventName} event for user: ${userName}`);
        return;
    }

    try
    {
        window.gtag('event', eventName, {
            user_id: userId,
            user_name: userName
        });
    }
    catch (error)
    {
        // Fail silently to avoid breaking auth flow
        console.warn(`[GA4 Tracking] Failed to track ${eventName} event:`, error);
    }
}

export async function login(username:string, password:string):Promise<User>
{
    let userId = CryptoJS.MD5(username + ":" + password).toString();
    let user = await auth(userId);
    trackUserEvent('login', userId, user.name);
    return user;
}

export async function getUser():Promise<User>
{
    if (loadingUser != null)
    {
        return loadingUser;
    }
    if (user != null)
    {
        // User is already cached, track auth_session_start if needed
        if (!hasSessionStarted() && typeof window !== "undefined")
        {
            let userId = window.localStorage.getItem("userId") || "";
            trackUserEvent('auth_session_start', userId, user.name);
            markSessionStarted();
        }
        return user;
    }
    else if (typeof window !== "undefined")
    {
        let userId = window.localStorage.getItem("userId") || "";
        loadingUser = auth(userId);
        let authenticatedUser = await loadingUser;

        // Track auth_session_start after successful authentication
        if (!hasSessionStarted())
        {
            trackUserEvent('auth_session_start', userId, authenticatedUser.name);
            markSessionStarted();
        }

        return authenticatedUser;
    }
    else
    {
        throw new Error("No user");
    }
}

export function logout()
{
    window.localStorage.removeItem("userId");
    window.location.reload();
}


async function auth(userId:string):Promise<User>
{
    if (!userId || userId.length != 32)
    {
        loadingUser = null;
        window.localStorage.setItem("userId", "");
        throw new Error("Invalid user");
    }
           
    try 
    {
        let result = await fetch("/users/" + userId + ".json?ts=" + (Date.now() - Date.now() % 86400000))
        let resultStr = await result.text();
        let resultObj = JSON.parse(resultStr);

        if (resultObj && resultObj.name && resultObj.token)
        {
            user = resultObj;
            window.localStorage.setItem("userId", userId);
            loadingUser = null;
            return user;
        }
        else
        {
            throw new Error("Invalid user");
        }
    }
    catch (e) 
    {
        loadingUser = null;
        throw new Error("Failed to auth");
    }
}